package com.solevo.dist.viewmodels

import com.solevo.dist.application.models.Invoice
import com.solevo.dist.application.models.InvoiceLines
import com.solevo.dist.application.models.Product
import com.solevo.dist.application.models.ProductItem
import com.solevo.dist.application.models.Transaction
import com.solevo.dist.application.models.toProductItem
import com.solevo.dist.navigation.Screen
import com.solevo.dist.network.api.invoice.InvoiceRemoteSource
import com.solevo.dist.network.api.products.ProductRemoteSource
import com.solevo.dist.network.api.transactions.TransactionRemoteSource
import com.solevo.dist.network.api.user.UserRemoteSource
import com.solevo.dist.pages.orderdetails.InvoicePayload
import com.solevo.dist.util.DateFormatter
import com.solevo.dist.util.AmountFormatter
import com.solevo.dist.util.StorageUrlResolver
import com.solevo.dist.util.StatusMapper
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.zip
import kotlinx.coroutines.launch
import kotlinx.datetime.Instant
import kotlinx.serialization.json.Json
import org.koin.mp.KoinPlatform.getKoin

data class InvoiceDetailsUi(
    val invoiceNumber: String = "",
    val docUrl: String? = null,
    val totalAmountText: String = "",
    val createdText: String = "",
    val dueDateText: String = "",
    val paidText: String = "",
    val remainingText: String = "",
    val statusLabel: String = "",
    val statusIcon: String = ResolvedIcons.closed,
    val statusColor: com.varabyte.kobweb.compose.ui.graphics.Color = com.varabyte.kobweb.compose.ui.graphics.Color.rgb(36,41,47),
    val statusColorHex: String = "#24292F",
    val items: List<ProductItem> = emptyList(),
)

private object ResolvedIcons {
    const val closed = com.solevo.dist.util.Res.Icon.OrderIcons.closed
}

class InvoiceDetailsViewModel(encodedJson: String) {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private val txSource: TransactionRemoteSource = getKoin().get()
    private val invSource: InvoiceRemoteSource = getKoin().get()
    private val productSource: ProductRemoteSource = getKoin().get()
    private val userRemoteSource: UserRemoteSource = getKoin().get()

    private val payload: InvoicePayload = Json.decodeFromString(encodedJson)

    private val _state = MutableStateFlow(InvoiceDetailsUi())
    val state: StateFlow<InvoiceDetailsUi> = _state.asStateFlow()

    @OptIn(ExperimentalCoroutinesApi::class)
    fun start() {
        scope.launch {
            userRemoteSource.currentUser()
                .filterNotNull()
                .flatMapLatest {
                    val invoiceFlow = invSource.getInvoice(payload.invoiceId)
                    val txFlow = txSource.getTransaction(payload.transactionId)

                    invoiceFlow.zip(txFlow) { invoice, tx -> invoice to tx }
                }
                .collectLatest { pair ->
                    val (invoice, tx) = pair
                    if (invoice == null && tx == null) return@collectLatest

                    console.log("[InvoiceVM] invoice=${invoice != null}, docMetadata=${invoice?.docMetadata}")
                    console.log("[InvoiceVM] url='${invoice?.docMetadata?.url}', filePath='${invoice?.docMetadata?.filePath}'")

                    val ui = buildUi(invoice, tx)
                    _state.value = ui

                    if (ui.docUrl.isNullOrBlank()) {
                        console.log("[InvoiceVM] docUrl is blank, trying to resolve from filePath...")
                        val resolved = StorageUrlResolver.resolveDocUrl(invoice?.docMetadata)
                        console.log("[InvoiceVM] resolved='$resolved'")
                        if (!resolved.isNullOrBlank()) {
                            _state.value = _state.value.copy(docUrl = resolved)
                        }
                    } else {
                        console.log("[InvoiceVM] docUrl already set: '${ui.docUrl}'")
                    }

                    val ids = invoice?.invoiceLines?.mapNotNull { it.productId }.orEmpty()
                    if (ids.isNotEmpty()) {
                        productSource.getProducts(ids).collectLatest { products ->
                            _state.value = _state.value.copy(items = getInvoiceItems(invoice?.invoiceLines.orEmpty(), products))
                        }
                    }
                }
        }
    }

    private fun buildUi(invoice: Invoice?, tx: Transaction?): InvoiceDetailsUi {
        val invoiceNumber = invoice?.invoiceNumber ?: tx?.invoiceNumber ?: payload.invoiceId
        val statusInfo = tx?.invoiceStatus?.let { StatusMapper.mapInvoiceStatus(it) }
            ?: StatusMapper.mapInvoiceStatus(com.solevo.dist.application.models.InvoiceStatus.Closed)
        
        // Convert StatusInfo to Color for UI
        val statusColor = when (statusInfo.colorHex) {
            "#DD3636" -> com.varabyte.kobweb.compose.ui.graphics.Color.rgb(221, 54, 54)
            "#B17700" -> com.varabyte.kobweb.compose.ui.graphics.Color.rgb(177, 119, 0)
            "#01A36C" -> com.varabyte.kobweb.compose.ui.graphics.Color.rgb(1, 163, 108)
            else -> com.varabyte.kobweb.compose.ui.graphics.Color.rgb(36, 41, 47)
        }
        
        return InvoiceDetailsUi(
            invoiceNumber = invoiceNumber,
            docUrl = invoice?.docMetadata?.url,
            totalAmountText = tx?.originalAmount?.let { AmountFormatter.formatAmountWithCurrency(kotlin.math.abs(it)) } ?: "",
            createdText = DateFormatter.formatAsDayMonthYear(tx?.date),
            dueDateText = DateFormatter.formatAsDayMonthYear(tx?.invoiceDueDate ?: invoice?.dueDate),
            paidText = tx?.amount?.let { AmountFormatter.formatAmountWithCurrency(kotlin.math.abs(it)) } ?: "",
            remainingText = tx?.remainingAmount?.let { AmountFormatter.formatAmountWithCurrency(kotlin.math.abs(it)) } ?: "",
            statusLabel = statusInfo.label,
            statusIcon = statusInfo.iconSrc ?: ResolvedIcons.closed,
            statusColor = statusColor,
            statusColorHex = statusInfo.colorHex,
            items = getInvoiceItems(invoice?.invoiceLines.orEmpty())
        )
    }

    private fun getInvoiceItems(invoiceLines: List<InvoiceLines>, products: List<Product> = emptyList()): List<ProductItem> {
        return invoiceLines.map { line ->
            val product = products.firstOrNull { line.productId == it.productId }
            line.toProductItem(product?.getProductName() ?: line.description ?: "")
        }.sortedBy { it.lineNumber.toInt() }
    }
}


